import { useEffect, useState } from 'react'
import { api } from '../api/client'
import type { DailyLog, Project } from '../types'
import { fmtDate, todayISO } from '../lib/format'

export default function DailyLogTab({ project }: { project: Project }) {
  const [logs, setLogs] = useState<DailyLog[]>([])
  const [draft, setDraft] = useState<Partial<DailyLog>>({ date: todayISO() })

  useEffect(() => {
    api.dailyLogs.list(project.id).then(setLogs)
  }, [project.id])

  async function add() {
    if (!draft.work_completed && !draft.notes) return
    const log = await api.dailyLogs.create(project.id, draft)
    setLogs((p) => [log, ...p])
    setDraft({ date: todayISO() })
  }

  async function remove(id: string) {
    if (!window.confirm('Delete this log entry? This cannot be undone.')) return
    await api.dailyLogs.delete(id)
    setLogs((p) => p.filter((x) => x.id !== id))
  }

  return (
    <section>
      <h2>Daily Site Log</h2>
      <p className="muted small mb-3">End-of-day site walk capture. {logs.length} entries.</p>

      <div className="card mb-3">
        <div className="form-grid-2">
          <label>
            <span className="form-label">Date</span>
            <input
              type="date"
              value={draft.date ?? todayISO()}
              onChange={(e) => setDraft({ ...draft, date: e.target.value })}
            />
          </label>
          <label>
            <span className="form-label">Weather</span>
            <input
              value={draft.weather ?? ''}
              onChange={(e) => setDraft({ ...draft, weather: e.target.value })}
              placeholder="Sunny, 62°F"
            />
          </label>
        </div>
        <label className="block">
          <span className="form-label">Crew on site</span>
          <input
            value={draft.crew_onsite ?? ''}
            onChange={(e) => setDraft({ ...draft, crew_onsite: e.target.value })}
            placeholder="Suburban Framing 4 men, Andres Electric 2 men"
          />
        </label>
        <label className="block">
          <span className="form-label">Work completed</span>
          <textarea
            rows={3}
            value={draft.work_completed ?? ''}
            onChange={(e) => setDraft({ ...draft, work_completed: e.target.value })}
            placeholder="Sheathing complete on east wall. Trusses delivered."
          />
        </label>
        <label className="block">
          <span className="form-label">Issues</span>
          <textarea
            rows={2}
            value={draft.issues ?? ''}
            onChange={(e) => setDraft({ ...draft, issues: e.target.value })}
            placeholder="Window 7 arrived damaged. Reordered."
          />
        </label>
        <button className="btn-primary" onClick={add}>
          Save Log Entry
        </button>
      </div>

      {logs.map((log) => (
        <div key={log.id} className="card mb-2">
          <div className="row-between">
            <div className="row-title">{fmtDate(log.date)}</div>
            <button className="btn-ghost small" onClick={() => remove(log.id)}>
              Delete
            </button>
          </div>
          {log.weather && <div className="muted small">Weather: {log.weather}</div>}
          {log.crew_onsite && <div className="muted small">Crew: {log.crew_onsite}</div>}
          {log.work_completed && (
            <div className="mt-2">
              <strong className="muted small">Work:</strong> {log.work_completed}
            </div>
          )}
          {log.issues && (
            <div className="mt-2 danger">
              <strong>Issues:</strong> {log.issues}
            </div>
          )}
        </div>
      ))}
    </section>
  )
}
